import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import type { BudgetPrice, BudgetPriceRow, BudgetTariffType } from "../types";
import { budgetPriceRowToBudgetPrice } from "../types";

export function useBudgetPrices(ownerId?: string) {
  const [prices, setPrices] = useState<BudgetPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // FIXÉ: Ne pas charger si ownerId pas encore connu
      if (!ownerId) {
        setPrices([]);
        setLoading(false);
        return;
      }

      // Isolation multi-tenant via RLS (budget_prices n'a pas de colonne owner_id ; l'accès est filtré via stock_regions.owner_id)
      const { data, error: e } = await supabase
        .from("budget_prices")
        .select("*")
        .order("stock_region_id", { ascending: true })
        .order("inventory_id", { ascending: true });

      if (e) throw e;
      const rows = (data ?? []) as BudgetPriceRow[];
      setPrices(rows.map(budgetPriceRowToBudgetPrice));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur chargement budget");
      setPrices([]);
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const getPrice = useCallback(
    (regionId: string, itemId: string, tariffType: BudgetTariffType): number => {
      const row = prices.find(
        (p) =>
          p.stockRegionId === regionId &&
          p.inventoryId === itemId &&
          p.tariffType === tariffType
      );
      return row?.unitPriceCents ?? 0;
    },
    [prices]
  );

  const setPrice = useCallback(
    async (
      regionId: string,
      itemId: string,
      tariffType: BudgetTariffType,
      cents: number
    ): Promise<{ data: BudgetPrice | null; error: string | null }> => {
      // FIXÉ: Vérification ownerId avant modification
      if (!ownerId) return { data: null, error: "Non autorisé" };

      try {
        const { data, error: e } = await supabase
          .from("budget_prices")
          .upsert(
            {
              stock_region_id: regionId,
              inventory_id: itemId,
              tariff_type: tariffType,
              unit_price_cents: cents,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "stock_region_id,inventory_id,tariff_type" }
          )
          .select()
          .single();

        if (e) throw e;
        const price = budgetPriceRowToBudgetPrice(data as BudgetPriceRow);

        setPrices((prev) => {
          const rest = prev.filter(
            (p) =>
              !(
                p.stockRegionId === regionId &&
                p.inventoryId === itemId &&
                p.tariffType === tariffType
              )
          );
          return [...rest, price].sort(
            (a, b) =>
              a.stockRegionId.localeCompare(b.stockRegionId) ||
              a.inventoryId.localeCompare(b.inventoryId)
          );
        });
        return { data: price, error: null };
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Erreur enregistrement prix";
        return { data: null, error: msg };
      }
    },
    [ownerId]
  );

  return { prices, loading, error, refetch: fetchAll, getPrice, setPrice };
}
